'use client';

import { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import EnrollmentForm from '@/components/EnrollmentForm';
import EnrollmentModal from '@/components/EnrollmentModal';

interface ProgramHeroProps {
  program: {
    title: string;
    tagline: string;
    duration: string;
    level: string;
    enrolledStudents: number;
    rating: number;
    heroImage: string;
    heroImageAlt: string;
  };
}

export default function ProgramHero({ program }: ProgramHeroProps) {
  const [isHydrated, setIsHydrated] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleEnrollClick = () => {
    if (!isHydrated) return;
    setIsModalOpen(true);
  };

  return (
    <section className="relative bg-gradient-to-br from-primary/5 via-sky-light/10 to-accent/5 pt-24 pb-16 overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 bg-primary rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 px-4 py-2 bg-primary/10 rounded-full">
              <Icon name="AcademicCapIcon" size={20} variant="solid" className="text-primary" />
              <span className="text-sm font-semibold text-primary">Professional Certification Program</span>
            </div>

            <h1 className="text-4xl md:text-5xl lg:text-6xl font-headline font-bold text-foreground leading-tight">
              {program.title}
            </h1>

            <p className="text-xl text-muted-foreground font-body">
              {program.tagline}
            </p>

            <div className="flex flex-wrap gap-6 pt-4">
              <div className="flex items-center space-x-2">
                <Icon name="ClockIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">{program.duration}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="SignalIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">{program.level}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="UsersIcon" size={20} variant="outline" className="text-primary" />
                <span className="text-sm font-medium text-foreground">{program.enrolledStudents.toLocaleString()}+ Students</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="StarIcon" size={20} variant="solid" className="text-warning" />
                <span className="text-sm font-medium text-foreground">{program.rating} Rating</span>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-4 pt-6">
              <button
                onClick={handleEnrollClick}
                className="px-8 py-4 bg-gradient-to-r from-primary to-accent text-primary-foreground rounded-lg font-headline font-semibold text-lg shadow-sky-md hover:shadow-sky-lg transition-all duration-300 hover:scale-105"
              >
                Enquire
              </button>
              <button
                onClick={() => isHydrated && window.scrollTo({ top: document.getElementById('curriculum')?.offsetTop || 0, behavior: 'smooth' })}
                className="px-8 py-4 bg-card border-2 border-primary text-primary rounded-lg font-headline font-semibold text-lg hover:bg-primary/5 transition-all duration-300"
              >
                View Curriculum
              </button>
            </div>
          </div>

          <div className="relative">
            <div className="relative rounded-2xl overflow-hidden shadow-sky-lg">
              <AppImage
                src={program.heroImage}
                alt={program.heroImageAlt}
                className="w-full h-auto"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary/20 to-transparent"></div>
            </div>
            <div className="absolute -bottom-6 -right-6 w-32 h-32 bg-accent/20 rounded-full blur-2xl"></div>
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-primary/20 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>

      {/* Enrollment Modal */}
      <EnrollmentModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        <EnrollmentForm onClose={() => setIsModalOpen(false)} />
      </EnrollmentModal>
    </section>
  );
}